/**
 * **An account name is free text, and a bookkeeper puts the account number in it** (Thomas, 2026-10-06, of
 * his own books: *account names from the books carry full bank account numbers and e-mail addresses*).
 *
 * Autho holds the name as the source wrote it — that is what provenance means, and the Ledger screen shows a
 * person their own books. What changes here is what an **agent** is handed: a bank account number in an answer
 * is a credential-shaped thing travelling to a model and whatever the model writes next, and the last four
 * digits are enough to tell two accounts apart, which is the only reason the number is in the name.
 *
 * Norwegian bank account numbers are eleven digits, usually written `1234.56.78901` or `12345678901`. An IBAN
 * is `NO` and thirteen more characters. Both are masked to their last four. Shorter digit runs are left alone:
 * an account name may carry a year, a department number or a rate, and masking those makes names unreadable
 * for nothing.
 */

/**
 * **Where a number must start and end to be masked at all: not inside a longer token.**
 *
 * `\b` was the first answer and it is wrong, because a word boundary sits on either side of a **hyphen**. A
 * uuid's last group is twelve characters, and one that happens to be all digits —
 * `9f81f674-8806-4bcd-8150-035757252298` — came back as `9f81f674-8806-4bcd-8150-•••• 2298`: an identifier
 * destroyed, so an agent could not cite the record it had just been handed or pass the id to the next tool.
 * It failed only when the random group drew no letters, which is roughly one id in two hundred, so the suite
 * passed on one tree and failed on the next with nothing between them but luck.
 *
 * So a run is masked only where neither neighbour is a word character or a hyphen. A digit run inside any
 * larger identifier — a uuid, a hex digest, a hyphenated key — is left exactly as it arrived. The cost, said
 * out loud: an account number written straight after a hyphen is not masked. A number that must survive is
 * worth more than one that might.
 */
const NOT_IN_A_TOKEN_BEFORE = '(?<![\\w-])';
const NOT_IN_A_TOKEN_AFTER = '(?![\\w-])';

/** Eleven digits, with or without the dots a Norwegian account number is usually written with. */
const KONTONUMMER = new RegExp(
  `${NOT_IN_A_TOKEN_BEFORE}(\\d{4})[.\\s]?(\\d{2})[.\\s]?(\\d{5})${NOT_IN_A_TOKEN_AFTER}`,
  'g',
);
/** A Norwegian IBAN: NO, two check digits, eleven more. Any case, with or without spaces. */
const IBAN = new RegExp(
  `${NOT_IN_A_TOKEN_BEFORE}NO\\s?\\d{2}(?:\\s?\\d{4}){2}\\s?\\d{3}${NOT_IN_A_TOKEN_AFTER}`,
  'gi',
);
/**
 * A long run of digits that is neither of the above — a foreign account, a card, a reference.
 *
 * **Eleven, not nine.** Nine would also match a Norwegian organisation number, and this pass runs over every
 * string in every agent answer: `org.entities` would have come back with `•••• 5002` where a company's number
 * belongs (Thomas, 2026-10-07). Eleven is a Norwegian account number's own length and the floor for a card.
 * An eleven-digit voucher reference may be masked by this, which is the cost Thomas accepted and the right way
 * round: a masked reference is readable as masked, an account number out in the open is not recoverable.
 */
const LONG_RUN = new RegExp(`${NOT_IN_A_TOKEN_BEFORE}\\d{11,}${NOT_IN_A_TOKEN_AFTER}`, 'g');

const keepLastFour = (digits: string) => `•••• ${digits.slice(-4)}`;

/**
 * The text with anything account-number-shaped reduced to its last four digits. Order matters: the IBAN and
 * the eleven-digit forms are matched before the catch-all, so a Norwegian number is masked as one thing rather
 * than having its tail eaten by the general rule.
 */
export function maskAccountNumbers(text: string): string {
  return text
    .replace(IBAN, (match) => keepLastFour(match.replace(/\D/g, '')))
    .replace(KONTONUMMER, (_match, a: string, b: string, c: string) => keepLastFour(`${a}${b}${c}`))
    .replace(LONG_RUN, (match) => keepLastFour(match));
}

/**
 * **Every string in an answer, not every field somebody remembered to name.** Field-level masking is how an
 * account number reached an agent in the first place: `org.accounts` and `org.financials` were masked by hand
 * and `org.bank.balances` returned `iban` whole (Thomas, 2026-10-07). A number also hides inside free text —
 * an account's name from the books reads *Folio (digits)* — so this walks the whole answer and masks strings
 * wherever they sit.
 *
 * Keys are not touched, and neither are numbers or dates: only string **values**. A new tool cannot miss this
 * by forgetting a field, because it does not name any.
 */
export function maskAnswer<T>(value: T): T {
  if (typeof value === 'string') return maskAccountNumbers(value) as unknown as T;
  if (Array.isArray(value)) return value.map((one) => maskAnswer(one)) as unknown as T;
  if (isPlainObject(value))
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, maskAnswer(v)])) as T;
  return value;
}

/**
 * **A plain object, and nothing that merely is one.** Rebuilding every object through `Object.entries` and
 * `Object.fromEntries` keeps the keys and throws the class away: a `Date` came back as `{}`, and the first
 * caller to do arithmetic on it got *Invalid time value* rather than a masked answer. A `Date`, a `Buffer`, a
 * `Map`, a decimal — none of them can hold an account number in a way this pass could find, and all of them
 * are destroyed by copying. So the walk descends into what JSON is made of and leaves the rest as it found it.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value) as object | null;
  return proto === Object.prototype || proto === null;
}

/** Null in, null out: an account may have no name, and a name is not invented to mask it. */
export function maskAccountNumbersOrNull(text: string | null): string | null {
  return text === null ? null : maskAccountNumbers(text);
}
